import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ComplaintService from '../services/complaint.service';
import PreviousComplaintsModal from './PreviousComplaintsModalFixed';
import '../styles/ComplaintForm.css';

interface Category {
  id: number;
  title: string;
}

interface ComplaintFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const ComplaintSchema = Yup.object().shape({
  category_id: Yup.number()
    .typeError('Category must be a number')
    .required('Category is required'),
  subject: Yup.string()
    .min(5, 'Title is too short')
    .max(100, 'Title is too long')
    .required('Title is required'),
  description: Yup.string()
    .min(10, 'Description is too short')
    .max(1000, 'Description is too long')
    .required('Description is required')
});

const NewComplaintForm: React.FC<ComplaintFormProps> = ({ onSuccess, onCancel }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [showPreviousComplaints, setShowPreviousComplaints] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>('');
  const [showCategoryAlert, setShowCategoryAlert] = useState(false);

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const fetchCategories = async (retryCount = 2) => {
    setLoadingCategories(true);

    for (let attempt = 0; attempt <= retryCount; attempt++) {
      try {
        const response = await ComplaintService.getCategories();
        const fetchedCategories = response?.data || [];
        setCategories(fetchedCategories);

        if (fetchedCategories.length === 0 && attempt < retryCount) {
          await delay(600);
          continue;
        }

        if (fetchedCategories.length === 0) {
          setError('No complaint categories are available right now. Please try again.');
        } else {
          setError(null);
        }

        setLoadingCategories(false);
        return;
      } catch (err: any) {
        if (attempt < retryCount) {
          await delay(600);
          continue;
        }

        setError('Failed to load categories. Please try again later.');
        setLoadingCategories(false);
        return;
      }
    }

    setLoadingCategories(false);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    
    if (file) {
      setPhotoFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>, setFieldValue: (field: string, value: any) => void) => {
    const categoryId = parseInt(e.target.value, 10);
    setFieldValue('category_id', e.target.value);
    
    if (categoryId) {
      // Find the category name
      const category = categories.find(cat => cat.id === categoryId);
      setSelectedCategoryId(categoryId);
      setSelectedCategoryName(category?.title || 'Selected Category');
      
      // Show the alert that allows checking previous complaints
      setShowCategoryAlert(true);
    } else {
      setSelectedCategoryId(null);
      setShowCategoryAlert(false);
    }
  };
  
  const handleViewPreviousComplaints = () => {
    setShowPreviousComplaints(true);
  };
  
  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    setLoading(true);
    setError(null);
    
    try {
      // Ensure category_id is converted to a number
      const formData = {
        ...values,
        category_id: parseInt(values.category_id, 10)
      };
      
      await ComplaintService.createComplaint(formData, photoFile);
      onSuccess();
    } catch (err: any) {
      console.error('Error submitting complaint:', err);
      setError(err.response?.data?.message || 'Failed to create complaint. Please try again.');
      setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="complaint-form-container">
      <h2>Create New Maintenance Request</h2>
      
      {error && <div className="alert alert-danger">{error}</div>}
      
      {showCategoryAlert && (
        <div className="alert alert-info alert-dismissible fade show" role="alert">
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <strong>Want to check existing complaints?</strong> There might be similar complaints in this category.
            </span>            <button 
              type="button" 
              className="btn btn-sm btn-danger ms-2"
              onClick={handleViewPreviousComplaints}
            >
              View Previous Complaints
            </button>
          </div>
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setShowCategoryAlert(false)} 
            aria-label="Close"
          ></button>
        </div>
      )}
      
      <Formik
        initialValues={{
          category_id: '',
          subject: '',
          description: ''
        }}
        validationSchema={ComplaintSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, setFieldValue }) => (
          <Form>
            <div className="form-group">
              <label htmlFor="category_id">Category *</label>
              <Field 
                as="select" 
                name="category_id" 
                className={`form-control ${errors.category_id && touched.category_id ? 'is-invalid' : ''}`}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleCategoryChange(e, setFieldValue)}
                disabled={loadingCategories}
              >
                <option value="">{loadingCategories ? 'Loading categories...' : 'Select a category'}</option>
                {categories && categories.length > 0 ? (
                  categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.title}</option>
                  ))
                ) : (
                  <option disabled>No categories available</option>
                )}
              </Field>
              <ErrorMessage name="category_id" component="div" className="invalid-feedback" />
              {!loadingCategories && categories.length === 0 && (
                <button
                  type="button"
                  className="btn btn-link p-0 mt-2"
                  onClick={() => fetchCategories()}
                >
                  Retry loading categories
                </button>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="subject">Title *</label>
              <Field 
                type="text" 
                name="subject" 
                className={`form-control ${errors.subject && touched.subject ? 'is-invalid' : ''}`} 
                placeholder="Briefly describe the issue"
              />
              <ErrorMessage name="subject" component="div" className="invalid-feedback" />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <Field 
                as="textarea" 
                name="description" 
                className={`form-control ${errors.description && touched.description ? 'is-invalid' : ''}`} 
                rows="5"
                placeholder="Provide detailed information about the issue"
              />
              <ErrorMessage name="description" component="div" className="invalid-feedback" />
            </div>
            
            <div className="form-group">
              <label htmlFor="photo">Photo (Optional)</label>
              <input 
                type="file" 
                id="photo"
                accept="image/*"
                className="form-control" 
                onChange={handlePhotoChange}
              />
              <small className="form-text text-muted">Upload a photo of the issue (max 5MB)</small>
              
              {photoPreview && (
                <div className="photo-preview">
                  <img src={photoPreview} alt="Preview" />
                </div>
              )}
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onCancel}
                disabled={isSubmitting || loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={isSubmitting || loading}
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
      
      {showPreviousComplaints && selectedCategoryId && (
        <PreviousComplaintsModal 
          categoryId={selectedCategoryId}
          categoryName={selectedCategoryName}
          onClose={() => setShowPreviousComplaints(false)}
        />
      )}
    </div>
  );
};

export default NewComplaintForm;
